/**
 * كيف يعمل نطاق — الصفحة المرجعية للزائر الجديد.
 *
 * الجولة تلقّن في دقيقة؛ وهذه الصفحة تستقبل من أراد التفصيل، ومن دخل الموقع
 * من رابط مباشر فلم يرَ الجولة أصلاً.
 *
 * قاعدتان في محتواها:
 *   ١. **لا رقم بيانات مكتوب هنا.** الأرقام تُقرأ من الشاشات التي تحسبها؛ ونصّ
 *      يحمل رقماً منسوخاً يشيخ بصمت ويكذب بلا أن ينبّه أحداً.
 *   ٢. **ما لا يفعله نطاق مذكور صراحةً وبنفس بروز ما يفعله.** المنتج الذي يعلن
 *      حدوده يُوثق به أكثر — وهذه هي القاعدة نفسها التي تحكم المحرك.
 *
 * لا منطق مجال هنا — عرض ونصّ فقط.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Disclaimer } from '@/components/data/Disclaimer';
import { replayGuideTour } from '@/components/guide/FirstVisitTour';
import { useJourney } from '@/components/guide/journey';

/** بند مرقّم — نفس نظام الصفحات النظامية كي تبدو الوثائق ورقة واحدة. */
function Section({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section style={{ marginBottom: 34 }}>
      <h2
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          margin: '0 0 12px',
          fontSize: 16,
          fontWeight: 600,
          color: color.text,
        }}
      >
        <span className="nq-mono" style={{ fontSize: 12, color: color.teal }}>
          {number}
        </span>
        {title}
      </h2>
      <div style={{ fontSize: 14, lineHeight: 1.95, color: color.textBody }}>{children}</div>
    </section>
  );
}

function P({ children }: { children: ReactNode }): JSX.Element {
  return <p style={{ margin: '0 0 12px' }}>{children}</p>;
}

/** صفّ تعريف — المصطلح كما يظهر في الشاشة، ثم معناه بلغة يومية. */
function Term({ term, children }: { term: string; children: ReactNode }): JSX.Element {
  return (
    <div style={{ paddingBlock: 13, borderTop: `1px solid ${color.hairlineSoft}` }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: color.text }}>{term}</div>
      <div style={{ marginTop: 4, fontSize: 13.5, lineHeight: 1.9, color: color.textSubtle }}>
        {children}
      </div>
    </div>
  );
}

export default function GuideRoute(): JSX.Element {
  const navigate = useNavigate();
  const journey = useJourney();

  function replay(): void {
    replayGuideTour();
    navigate('/');
  }

  function startJourney(): void {
    journey.start();
    navigate('/evaluate');
  }

  return (
    <div data-testid="guide-page" className="nq-enter">
      <PageHeader
        stamp="NITAQ / GUIDE"
        title="كيف يعمل نطاق"
        subtitle="صفحة واحدة تشرح المنتج لمن يفتحه أول مرة: ما يجاوب عنه، من أين أرقامه، ومتى يمتنع عن الإجابة."
        action={
          <button type="button" className="nq-btn nq-btn--ghost nq-btn--sm" onClick={replay}>
            أعد عرض الجولة
          </button>
        }
      />

      <article style={{ maxWidth: 720, padding: '30px 0 20px' }}>
        <Section number="١" title="السؤال الذي يجاوبه نطاق">
          <P>
            <strong>هل السعر المطلوب يطابق السوق — ووش الدليل؟</strong>
          </P>
          <P>
            تُدخل الحي والمساحة والسعر المطلوب، فيبحث نطاق عن الصفقات المسجّلة لقطع مشابهة في
            الحي نفسه، ويعرض النطاق الذي تقع فيه أسعارها — ثم يقول لك أين يقف سعرك منه.
          </P>
          <P>
            القاعدة الحاكمة للمنتج كله سطر واحد:{' '}
            <strong>إذا كانت البيانات لا تثبتها، نطاق لا يقولها.</strong>
          </P>
        </Section>

        <Section number="٢" title="من أين تأتي الأرقام">
          <P>
            المصدر الأساسي رسمي: <strong>صفقات موثّقة</strong> — بيع تمّ فعلاً وانتقلت ملكيته.
          </P>
          <P>
            الإعلانات العقارية شيء آخر: هي سعر <strong>طلب</strong> لا سعر{' '}
            <strong>تنفيذ</strong>، والفرق بينهما هو بالضبط ما جاء نطاق ليقيسه. لذلك أسعار
            الإعلانات <strong>لا تدخل</strong> في حساب النطاق السعري إطلاقاً.
          </P>
          <P>
            والتغطية اليوم: <strong>الرياض وجدة</strong> · <strong>أرض</strong> و
            <strong>مبنى سكني</strong> · <strong>بيع فقط</strong>.
          </P>
        </Section>

        <Section number="٣" title="الثلاثة التي تُقرأ في كل نتيجة">
          <Term term="النطاق السعري للسوق">
            حدّان يقع بينهما سعر السوق لقطعة مثل قطعتك. ليس رقماً واحداً لأن السوق نفسه ليس
            رقماً واحداً — قطعتان متجاورتان تُباعان بسعرين.
          </Term>
          <Term term="جملة الحكم">
            سطر واحد مباشر يقول أين يقف السعر المطلوب من النطاق: داخله، أو أقل منه بنسبة، أو
            أعلى منه بنسبة. ولا كلمة بعدها — الرقم يحكم، وأنت تقرّر.
          </Term>
          <Term term="قوة الدليل">
            كلمة واحدة — قوي أو متوسط أو ضعيف — ومعها عدد الصفقات المشابهة التي بُني عليها
            الرقم. وهي ليست رأياً في القطعة، بل وصفٌ لكمّ الأدلة التي وُجدت.
          </Term>
          <div style={{ paddingTop: 14, borderTop: `1px solid ${color.hairlineSoft}` }}>
            <P>
              وتحت هذه الثلاثة تجد <strong>الصفقات المشابهة</strong> نفسها معروضة — لأن الرقم
              الذي لا يُرى دليله رأيٌ في غلاف رياضي.
            </P>
          </div>
        </Section>

        <Section number="٤" title="متى يمتنع نطاق عن إعطائك رقماً">
          <P>
            أحياناً تُدخل قطعة فتقرأ: <strong>«ما نقدر نعطيك نطاقاً موثوقاً لهذي القطعة»</strong>.
          </P>
          <P>
            هذا ليس عطلاً في الموقع، ولا يعني أن الحي غير موجود. يعني أن الصفقات المسجّلة في
            ذلك الحي خلال المدة <strong>أقل من أن يُبنى عليها رقم</strong> — وسيُعرض لك العدد
            الذي وُجد فعلاً كي ترى السبب بنفسك.
          </P>
          <P>
            وهذا قرار مقصود لا نقص. المحرك مصمَّم بحيث <strong>لا يستطيع</strong> نشر نطاق
            ضعيف الأدلة، لأن الرقم الواثق فوق دليل هشّ أسوأ من الصمت.
          </P>
          <P>وحين يمتنع، تبقى أمامك ثلاثة طرق: مؤشرات الحي، والمراقبة، وحيّ مجاور.</P>
        </Section>

        <Section number="٥" title="ما لا يفعله نطاق — بصراحة">
          <Term term="لا يتنبأ بأسعار المستقبل">
            لا يوجد في المنتج محرك تنبؤ. ما تراه وصفٌ لما حدث في السوق، لا ادعاءٌ عمّا سيحدث.
          </Term>
          <Term term="لا يعرف خصائص القطعة نفسها">
            عرض الشارع، والزاوية، وقيود المخطط، وحالة المبنى — كلها تؤثر في السعر ولا تصل إلينا
            في البيانات. فالنطاق يصف السوق لا قطعتك بعينها.
          </Term>
          <Term term="لا يحسب عائداً">
            حساب العائد يحتاج بيانات إيجار، وهي ليست ضمن مصادر اليوم. فنطاق الآن{' '}
            <strong>حَكَم تسعير</strong>، لا أداة استثمار.
          </Term>
          <Term term="لا يعطي توصية">
            لن تقرأ هنا «اشترِ» ولا «ننصح». نعرض الأرقام ودليلها، والقرار قرارك.
          </Term>
        </Section>

        <Section number="٦" title="من أين تبدأ">
          <P>
            أسرع طريق لفهم المنتج أن تمشي فيه. الجولة الإرشادية تأخذك على الشاشات الفعلية
            خطوة خطوة، وتشرح لك النتيجة وهي أمامك — وتوقفها متى شئت.
          </P>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
            <button type="button" className="nq-btn nq-btn--primary" onClick={startJourney}>
              ابدأ الجولة الإرشادية
            </button>
            <button
              type="button"
              className="nq-btn nq-btn--ghost"
              onClick={() => navigate('/evaluate')}
            >
              قارن سعرك بنفسك
            </button>
            <button
              type="button"
              className="nq-btn nq-btn--ghost"
              onClick={() => navigate('/map')}
            >
              تصفّح الخريطة
            </button>
            <button
              type="button"
              className="nq-btn nq-btn--ghost"
              onClick={() => navigate('/opportunities')}
            >
              تصفّح الفرص
            </button>
          </div>
        </Section>

        <Disclaimer />
      </article>
    </div>
  );
}
